import React, { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import type { KpiCell, KeyCalloutRef, Callout } from "../../models/API";
import KpiStripEditor from "./KpiStripEditor";
import OverviewEditor from "./OverviewEditor";
import KeyCalloutsPicker from "./KeyCalloutsPicker";

interface SummaryEditorProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called with the structured data from all three sections.
   * The viewer is responsible for the optimistic update and invoking the
   * updateCalloutSummary mutation (FR-5.2).
   */
  onSave: (
    overview: string,
    kpiStrip: KpiCell[],
    keyCallouts: KeyCalloutRef[]
  ) => void;
  /** Initial overview text to pre-populate the Overview editor. */
  initialOverview: string;
  /** Initial KPI strip cells to pre-populate the KPI editor. */
  initialKpiStrip: KpiCell[];
  /** Initial key callout refs to pre-populate the Key Callouts picker. */
  initialKeyCallouts: KeyCalloutRef[];
  /** The full callout list for the current report (for the Key Callouts picker). */
  callouts: Callout[];
  /** True while a save is in flight; disables inputs/controls. */
  loading: boolean;
  /** Error indication from a failed save; shown as an alert. */
  error?: string | null;
}

/**
 * SummaryEditor v2 — structured edit form with three sections:
 *   1. KPI Strip Editor (add/remove/reorder KPI cells)
 *   2. Overview Editor (textarea with character counter)
 *   3. Key Callouts Picker (select up to 3 callouts by page/card)
 *
 * Replaces the old single-HTML-textarea editor (FR-5.1, FR-5.2, FR-5.3).
 */
const SummaryEditor: React.FC<SummaryEditorProps> = ({
  open,
  onClose,
  onSave,
  initialOverview,
  initialKpiStrip,
  initialKeyCallouts,
  callouts,
  loading,
  error,
}) => {
  const [overview, setOverview] = useState("");
  const [kpiStrip, setKpiStrip] = useState<KpiCell[]>([]);
  const [keyCallouts, setKeyCallouts] = useState<KeyCalloutRef[]>([]);

  // Pre-populate the form each time the dialog opens or initial values change.
  useEffect(() => {
    if (!open) return;
    setOverview(initialOverview ?? "");
    setKpiStrip(initialKpiStrip ?? []);
    setKeyCallouts(initialKeyCallouts ?? []);
  }, [open, initialOverview, initialKpiStrip, initialKeyCallouts]);

  const overviewTooLong = overview.length > 4000;

  // Save disabled while overview exceeds limit or while saving
  const saveDisabled = overviewTooLong || loading;

  const handleSubmit = () => {
    if (saveDisabled) return;
    onSave(overview, kpiStrip, keyCallouts);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Summary</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
      >
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}

        {/* Section 1: KPI Strip */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: "#1B3A6B" }}
          >
            KPI Strip
          </Typography>
          <KpiStripEditor cells={kpiStrip} onChange={setKpiStrip} />
        </Box>

        <Divider />

        {/* Section 2: Overview */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: "#1B3A6B" }}
          >
            Overview Narrative
          </Typography>
          <OverviewEditor value={overview} onChange={setOverview} />
        </Box>

        <Divider />

        {/* Section 3: Key Callouts */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: "#1B3A6B" }}
          >
            Key Callouts
          </Typography>
          <KeyCalloutsPicker
            selected={keyCallouts}
            callouts={callouts}
            onChange={setKeyCallouts}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saveDisabled}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Saving…
            </Box>
          ) : (
            "Save"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SummaryEditor;
